import React, { useState, useRef, useEffect } from "react";
import regionsPh from "./philAPI/regionsPh.json";
import provincePh from "./philAPI/provincePh.json";
import municipalityPh from "./philAPI/municipalityPh.json";
import barangayPh from "./philAPI/barangayPh.json";
import SearchSelect from "../../../../../components/searchSelect/SearchSelect";

const PermanentAddressSelector = ({
  object,
  onLocationChange,
  disabled = false,
  ...props
}) => {
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedBarangay, setSelectedBarangay] = useState("");
  const [regionsList, setRegionsList] = useState(regionsPh);
  const [provinceList, setProvinceList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [barangayList, setBarangayList] = useState([]);
  const wrapperRef = useRef();

  const hasInitialized = useRef(false);

  useEffect(() => {
    const hasData =
      object && Object.values(object).some((value) => value !== "");
    if (hasData && !hasInitialized.current) {
      const isSame =
        object.permanentRegion === selectedRegion &&
        object.permanentProvince === selectedProvince &&
        object.permanentCity === selectedCity &&
        object.permanentBrangay === selectedBarangay;

      if (!isSame) {
        setSelectedRegion(object.permanentRegion || "");
        setSelectedProvince(object.permanentProvince || "");
        setSelectedCity(object.permanentCity || "");
        setSelectedBarangay(object.permanentBrangay || "");
      }

      hasInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object]);

  useEffect(() => {
    if (!hasInitialized.current || disabled) return;

    const isSame =
      object.permanentRegion === selectedRegion &&
      object.permanentProvince === selectedProvince &&
      object.permanentCity === selectedCity &&
      object.permanentBrangay === selectedBarangay;

    if (!isSame) {
      onLocationChange({
        permanentRegion: selectedRegion,
        permanentProvince: selectedProvince,
        permanentCity: selectedCity,
        permanentBrangay: selectedBarangay,
      });
    }
  }, [
    selectedRegion,
    selectedProvince,
    selectedCity,
    selectedBarangay,
    onLocationChange,
    disabled,
  ]);

  // Only sync UI from parent when mirroring residential (disabled/manual entry off)
  useEffect(() => {
    if (!hasInitialized.current || !disabled) return;

    const region = object.permanentRegion || "";
    const province = object.permanentProvince || "";
    const city = object.permanentCity || "";
    const barangay = object.permanentBrangay || "";

    if (
      region !== selectedRegion ||
      province !== selectedProvince ||
      city !== selectedCity ||
      barangay !== selectedBarangay
    ) {
      setSelectedRegion(region);
      setSelectedProvince(province);
      setSelectedCity(city);
      setSelectedBarangay(barangay);
    }
  }, [
    disabled,
    object.permanentRegion,
    object.permanentProvince,
    object.permanentCity,
    object.permanentBrangay,
  ]);

  const clearSelection = (type) => {
    if (disabled) return;

    if (type === "region") {
      setSelectedRegion("");
      setSelectedProvince("");
      setSelectedCity("");
      setSelectedBarangay("");
      setProvinceList([]);
      setCityList([]);
      setBarangayList([]);
    } else if (type === "province") {
      setSelectedProvince("");
      setSelectedCity("");
      setSelectedBarangay("");
      setCityList([]);
      setBarangayList([]);
    } else if (type === "city") {
      setSelectedCity("");
      setSelectedBarangay("");
      setBarangayList([]);
    } else if (type === "barangay") {
      setSelectedBarangay("");
    }
  };

  const handleRegionsChange = (valueName, valueCode) => {
    if (disabled) return;

    const selectedRegion = valueCode;
    setSelectedRegion(valueName);
    setSelectedProvince("");
    setSelectedCity("");
    setSelectedBarangay("");

    let provinceLists = provincePh.map((province) => ({
      name: province.name,
      code: province.code,
      region: province.regionCode,
    }));

    const cityLists = municipalityPh.map((city) => ({
      name: city.name,
      code: city.code,
      province: city.provinceCode,
      region: city.regionCode,
    }));

    // Handle NCR special case
    if (selectedRegion === "130000000") {
      const filteredCityList = cityLists.filter(
        (city) => city.region === "130000000"
      );

      setCityList(filteredCityList);
      return;
    }

    const filteredProvinceList = provinceLists.filter((province) =>
      province.region.startsWith(selectedRegion)
    );

    setProvinceList(filteredProvinceList);
    setCityList([]);
  };

  const handleProvinceChange = (valueName, valueCode) => {
    if (disabled) return;

    const selectedProvince = valueCode;
    setSelectedProvince(valueName);
    setSelectedCity("");
    setSelectedBarangay("");

    const cityLists = municipalityPh.map((city) => ({
      name: city.name,
      code: city.code,
      province: city.provinceCode,
    }));

    const filteredCityList = cityLists.filter(
      (city) => city.province === selectedProvince
    );

    setCityList(filteredCityList);
  };

  const handleCityChange = (valueName, valueCode) => {
    if (disabled) return;

    const selectedCity = valueCode;
    setSelectedCity(valueName);
    setSelectedBarangay("");

    const barangayLists = barangayPh.map((barangay) => ({
      name: barangay.name,
      code: barangay.code,
      city: barangay.municipalityCode,
      city1: barangay.cityCode,
      city2: barangay.subMunicipalityCode,
    }));

    const filteredBarangayList = barangayLists.filter(
      (barangay) =>
        barangay.city === selectedCity ||
        barangay.city1 === selectedCity ||
        barangay.city2 === selectedCity
    );

    setBarangayList(filteredBarangayList);
  };

  const handleBarangayChange = (valueName) => {
    if (disabled) return;

    setSelectedBarangay(valueName);
  };

  return (
    <div ref={wrapperRef}>
      <div className="row">
        <div className="mb-3 col-12 col-md-4 p-1">
          <label className="mb-2 fs-sm" htmlFor="region">
            Regions <span style={{ color: "red" }}>*</span>
          </label>
          <SearchSelect
            {...props}
            disabled={disabled}
            focus={!disabled}
            value={selectedRegion.length > 0 ? selectedRegion : ""}
            onClickClear={() => clearSelection("region")}
            required={!disabled}
            onChange={(value) => handleRegionsChange(value.name, value.code)}
            options={regionsList.map((region) => ({
              label: region.name,
              value: { name: region.name, code: region.code },
            }))}
          />
        </div>

        {selectedRegion && selectedRegion !== "NCR" && (
          <div className="mb-3 col-12 col-md-4 p-1">
            <label className="mb-2 fs-sm" htmlFor="province">
              Province <span style={{ color: "red" }}>*</span>
            </label>
            <SearchSelect
              {...props}
              disabled={disabled}
              focus={!disabled}
              value={selectedProvince.length > 0 ? selectedProvince : ""}
              onClickClear={() => clearSelection("province")}
              required={!disabled}
              onChange={(value) => handleProvinceChange(value.name, value.code)}
              options={provinceList.map((province) => ({
                label: province.name,
                value: { name: province.name, code: province.code },
              }))}
            />
          </div>
        )}

        {(selectedProvince || selectedRegion === "NCR") && (
          <div className="mb-3 col-12 col-md-4 p-1">
            <label className="mb-2 fs-sm" htmlFor="city">
              City/Municipality <span style={{ color: "red" }}>*</span>
            </label>
            <SearchSelect
              {...props}
              disabled={disabled}
              focus={!disabled}
              value={selectedCity.length > 0 ? selectedCity : ""}
              onClickClear={() => clearSelection("city")}
              required={!disabled}
              onChange={(value) => handleCityChange(value.name, value.code)}
              options={cityList.map((city) => ({
                label: city.name,
                value: { name: city.name, code: city.code },
              }))}
            />
          </div>
        )}

        {selectedCity && (
          <div className="mb-3 col-12 col-md-4 p-1">
            <label className="mb-2 fs-sm" htmlFor="barangay">
              Barangay <span style={{ color: "red" }}>*</span>
            </label>
            <SearchSelect
              {...props}
              disabled={disabled}
              focus={!disabled}
              value={selectedBarangay.length > 0 ? selectedBarangay : ""}
              onClickClear={() => clearSelection("barangay")}
              required={!disabled}
              onChange={(value) => handleBarangayChange(value.name, value.code)}
              options={barangayList.map((barangay) => ({
                label: barangay.name,
                value: { name: barangay.name, code: barangay.code },
              }))}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PermanentAddressSelector;
